import React, {
  ReactElement,
  useState,
  useCallback,
  useEffect,
  SyntheticEvent,
  useMemo,
} from 'react';
import { Flex, Text } from 'rebass/styled-components';

import useQuestionColumns from '../../../Questions/useQuestionColumns';
import ChoiceQuestion from '../ChoiceQuestion';
import LinkQuestion from '../LinkQuestion';
import TextQuestion from '../TextQuestion';
import LookupQuestion from '../LookupQuestion';

import { FormInput, Table, SearchInput, FormSelect } from 'ui/patterns';
import { Button, Stack, Checkbox, Grid } from 'ui/components';

import { useGetQuestions } from 'hooks';

import * as Icon from 'assets/icons';

import { getRenderTypes } from '../formatQuestionData';
import { QuestionDefinitionType } from '../questionTypes';
import { getValue } from '@testing-library/user-event/dist/utils';

const sortItem = { sortKey: 'Question Type', sortOrder: 'asc' };

const questionSettings = ['Question Type', 'Field Name', 'Field Label'];

export default function QuestionDefinition(
  QuestionDefinitionProps: QuestionDefinitionType,
): ReactElement {
  const {
    showPanel,
    selectedQuestionType,
    setQuestionType,
    resetForm,
    currentQuestion,
    associatedPersons,
    register,
    control,
    Controller,
    watch,
    setValue,
    errors,
    getValues,
    reset,
    accountId,
    mappedColumns,
    setMappedColumns,
    questionTemplateOptions,
    setQuestionTemplateOptions,
    sections,
    setLookupDependency,
    lookupDependency,
    handleFormandQuestionSave,
    url,
    setUrl,
    setIsLookupFetching,
    errorMessage,
  } = QuestionDefinitionProps;

  const [columnsSearch, setColumnsSearch] = useState({}); // table header search
  const [currentSortItem, setCurrentSortItem] = useState(sortItem);
  const [keywordSearch, setKeywordSearch] = useState(''); //keyword search

  const permission = JSON.parse(localStorage.getItem('userPermissions') ?? '');

  const renderTypes = getRenderTypes(selectedQuestionType);

  const columns = useQuestionColumns(questionSettings, true);
  const {
    data: questionList,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isFetching,
  } = useGetQuestions({
    columnsSearch,
    sortItem: currentSortItem,
    keywordSearch,
  });
  useEffect(() => {
    if (!showPanel) {
      handleReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPanel]);

  const tableData = useMemo(() => {
    if (questionList) {
      return questionList.pages.map((dataItem: any) => dataItem.Data).flat();
    }
  }, [questionList]);

  const totalItems = useMemo(() => {
    if (questionList) {
      return questionList.pages[0].FilteredRecords;
    }
    return 0;
  }, [questionList]);

  const onEndReached = () => {
    if (tableData) {
      if (hasNextPage && tableData?.length < totalItems) fetchNextPage();
    }
  };

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch],
  );

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (
        currentSortItem.sortKey === item &&
        currentSortItem.sortOrder === 'asc'
      ) {
        sortOrder = 'desc';
      }
      setCurrentSortItem({
        sortKey: item,
        sortOrder,
      });
    },
    [setCurrentSortItem, currentSortItem],
  );

  const handleKeywordSearch = (value: string): void => {
    setKeywordSearch(value);
  };

  const handleReset = (): void => {
    setKeywordSearch('');
    setColumnsSearch({});
    setCurrentSortItem(sortItem);
    resetForm();
  };
  const handleRowClick = (row: any): void => {
    resetForm();
    let personAssociation = 0;
    if (row.original.PersonAssociation === null) personAssociation = 0;
    if (row.original.PersonAssociation === 0) personAssociation = 1;
    if (row.original.PersonAssociation === 1) personAssociation = 2;

    const questionParams = {
      ...row.original,
      QuestionTemplateId: row.original.Id,
      QuestionDetail: {
        ExternalDocumentUri: '',
        HelpText: '',
        Id: 0,
        ProcedureUri: '',
        Type: 0,
      },
      IsRequired: row.original?.QuestionTypeOption?.IsRequired,
      IsNeverSentToAccount:
        row.original?.QuestionTypeOption?.IsNeverSentToAccount,
      IsNotSentToAccountIfNoAnswer:
        row.original?.QuestionTypeOption?.IsNotSentToAccountIfNoAnswer,
      PersonAssociation: personAssociation,
      OriginalFieldLabel: row.original.FieldLabel,
    };
    setQuestionType(questionParams);
  };

  return (
    <Flex flexDirection="column" height="100%" sx={{ position: 'relative' }}>
      <Stack
        direction="y"
        gap={5}
        pb="50px"
        style={{
          flex: '1 1 auto',
          overflow: 'auto',
          height: '1px',
          paddingRight: '10px',
        }}
      >
        {Object.keys(currentQuestion).length === 0 && (
          <Stack direction="y" gap={3}>
            <Flex justifyContent="space-between" alignItems="center" mb={2}>
              <Text fontSize={1} color="text.body" sx={{ height: '20px' }}>
                {tableData && tableData.length > 0 && (
                  <Text mb={2}>{`Showing ${tableData.length} of ${totalItems}${
                    isFetching ? ' | Processing' : ''
                  }`}</Text>
                )}
              </Text>
              <Stack
                direction="x"
                gap={2}
                style={{ justifyContent: 'flex-end' }}
              >
                <SearchInput
                  placeholder="Search questions"
                  value={keywordSearch}
                  handleChange={handleKeywordSearch}
                />
                <Button
                  iconBefore={<Icon.Refresh title="" ariaHidden="true" />}
                  variant="secondary"
                  onClick={handleReset}
                >
                  Reset All
                </Button>
              </Stack>
            </Flex>
            <Table
              columns={columns}
              data={tableData}
              isLoading={isLoading}
              headerUpdateCallBack={headerUpdateCallBack}
              handleSortChange={handleSortChange}
              sortedItem={currentSortItem}
              columnsSearch={columnsSearch}
              height="300px"
              noOptions
              onRowClick={handleRowClick}
              selectedRowId={selectedQuestionType?.Id}
              onEndReached={onEndReached}
              isFetching={!!isFetchingNextPage}
            />
          </Stack>
        )}
        {selectedQuestionType &&
          Object.keys(selectedQuestionType).length !== 0 &&
          !renderTypes && (
            <>
              <Grid minColWidth="300px" gap={3}>
                <FormInput
                  label="Field Name"
                  disabled
                  register={register}
                  name="FieldName"
                />
                <FormInput
                  label="Original Field Label"
                  disabled
                  value={selectedQuestionType?.OriginalFieldLabel||'' }
                />
                <FormInput
                  label="Description of Use"
                  disabled
                  register={register}
                  name="Description"
                />
                <FormInput
                  label="Field Label"
                  register={register}
                  name="UpdatedFieldLabel"
                />
                <Controller
                  render={(props: {
                    onChange: (value: number) => void;
                    value: number;
                  }) => {
                    return (
                      <FormSelect
                        disabled={
                          !selectedQuestionType?.QuestionType
                            ?.IsQuestionAssociable
                        }
                        value={props.value ?? 0}
                        label="Person Association"
                        noSelectOption
                        options={associatedPersons}
                        onChange={(e: SyntheticEvent<HTMLSelectElement>) => {
                          const value = parseInt(e.currentTarget.value, 10);
                          props.onChange(value);
                        }}
                      />
                    );
                  }}
                  defaultValue={selectedQuestionType?.PersonAssociation || 0}
                  name="PersonAssociation"
                  control={control}
                />
              </Grid>
              <Grid minColWidth="250px" gap={3}>
                <Checkbox
                  label="Required"
                  register={register}
                  name="IsRequired"
                  disabled={
                    selectedQuestionType?.QuestionType?.Description ===
                    'Display Only'
                      ? true
                      : false
                  }
                />
                <Checkbox
                  label="Never send to Account"
                  register={register}
                  name="IsNeverSentToAccount"
                />
                <Checkbox
                  label="If no answer, don't send to Account"
                  register={register}
                  name="IsNotSentToAccountIfNoAnswer"
                />
                {selectedQuestionType?.QuestionType?.Description ===
                  'Display Only' &&
                  (permission.isRelationshipManager || permission.isAdmin) && (
                    <Checkbox
                      label="Emphasize Question"
                      register={register}
                      name="IsFlagged"
                      title="Highlight the display questions in call-center"
                    />
                  )}
              </Grid>
            </>
          )}
        {selectedQuestionType?.QuestionType?.Description === 'Lookup' && (
          <LookupQuestion
            accountId={accountId}
            selectedQuestionType={selectedQuestionType}
            associatedPersons={associatedPersons}
            register={register}
            control={control}
            Controller={Controller}
            watch={watch}
            mappedColumns={mappedColumns}
            setMappedColumns={setMappedColumns}
            sections={sections}
            currentQuestion={currentQuestion}
            setLookupDependency={setLookupDependency}
            lookupDependency={lookupDependency}
            handleFormandQuestionSave={handleFormandQuestionSave}
            setIsLookupFetching={setIsLookupFetching}
          />
        )}

        {selectedQuestionType?.QuestionType?.Description === 'Choice' && (
          <ChoiceQuestion
            selectedQuestionType={selectedQuestionType}
            associatedPersons={associatedPersons}
            register={register}
            control={control}
            Controller={Controller}
            watch={watch}
            setValue={setValue}
            questionTemplateOptions={questionTemplateOptions}
            setQuestionTemplateOptions={setQuestionTemplateOptions}
            sections={sections}
            currentQuestion={currentQuestion}
            handleFormandQuestionSave={handleFormandQuestionSave}
          />
        )}

        {selectedQuestionType?.QuestionType?.Description === 'Text' && (
          <TextQuestion
            selectedQuestionType={selectedQuestionType}
            associatedPersons={associatedPersons}
            register={register}
            control={control}
            Controller={Controller}
            watch={watch}
            setValue={setValue}
            errors={errors}
            getValues={getValues}
            reset={reset}
            url={url}
            setUrl={setUrl}
            errorMessage={errorMessage}
          />
        )}

        {selectedQuestionType?.QuestionType?.Description === 'Link' && (
          <LinkQuestion
            selectedQuestionType={selectedQuestionType}
            associatedPersons={associatedPersons}
            register={register}
            control={control}
            Controller={Controller}
            watch={watch}
            setValue={setValue}
          />
        )}
      </Stack>
    </Flex>
  );
}
